import * as React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Youtube, Linkedin } from "lucide-react";
import type { LucideProps } from "lucide-react";
import LogoSebrae from "@/components/brand/LogoSebrae";
import { QuizExitDialog } from "@/components/brand/QuizExitDialog";
import { useQuizExitConfirmation } from "@/lib";
import type { FooterData } from "@/types";

interface FooterProps {
  data?: FooterData;
}

// Fallback data
const defaultNavLinks = [
  { title: "Sebrae Minas Gerais", url: "#" },
  { title: "Quem Somos", url: "#" },
  { title: "Resultados", url: "/results" },
  { title: "Notícias", url: "#" },
  { title: "Transparência", url: "#" },
];

const getSocialIcon = (name: string, props: LucideProps) => {
  const normalize = (s: string) => s.toLowerCase().trim();
  const n = normalize(name);
  if (n.includes('instagram')) return <Instagram {...props} />;
  if (n.includes('facebook')) return <Facebook {...props} />;
  if (n.includes('twitter') || n.includes('x')) return <Twitter {...props} />;
  if (n.includes('youtube')) return <Youtube {...props} />;
  if (n.includes('linkedin')) return <Linkedin {...props} />;
  return null;
};

// Static social links map for fallback
const staticSocialLinks = [
  { title: 'Instagram', href: "#", icon: Instagram },
  { title: 'Facebook', href: "#", icon: Facebook },
  { title: 'Twitter', href: "#", icon: Twitter },
  { title: 'Youtube', href: "#", icon: Youtube },
  { title: 'Linkedin', href: "#", icon: Linkedin },
];

function Footer({ data }: FooterProps) {
  const {
    isQuiz,
    modalOpen,
    setModalOpen,
    handleMenuClick,
    handleConfirm,
    handleCancel,
  } = useQuizExitConfirmation();

  // Prefer dynamic data, fallback to static/default
  const navLinks = data?.menu?.map(m => ({ title: m.title, url: m.url })) || defaultNavLinks;
  const phoneNumber = data?.phone?.[0]?.phone || "0800 570 0800";

  // For socials, if specific icon names come from DB, we'd need a mapper.
  // Assuming Backend sends title/url. We map defaults if data exists.
  const socialItems = data?.social || [];
  const hasDynamicSocials = socialItems.length > 0;

  return (
    <footer className="bg-gray-900 text-white font-poppins px-8">
      <div className="container mx-auto px-4 py-8">
        {/* Navegação Superior do Rodapé */}
        <nav className="flex sm:flex-col lg:flex-row md:flex-row flex-wrap justify-center md:justify-between items-center mb-8">
          {navLinks.map((link) => (
            isQuiz ? (
              <a
                key={link.title}
                href={link.url}
                className="px-3 py-1 text-sm hover:underline"
                onClick={(e) => handleMenuClick(e, link.url)}
                tabIndex={0}
              >
                {link.title}
              </a>
            ) : (
              <Link key={link.title} to={link.url} className="px-3 py-1 text-sm hover:underline">
                {link.title}
              </Link>
            )
          ))}
        </nav>

        {/* Seção Principal do Rodapé */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center text-center md:text-left">
          {/* Coluna do Telefone */}
          <div className="flex justify-center md:justify-start">
            <a href={`tel:${phoneNumber.replace(/\D/g, '')}`} className="text-xl font-normal tracking-wider hover:text-gray-300">
              {phoneNumber}
            </a>
          </div>

          {/* Coluna Central (Ícones Sociais e Copyright) */}
          <div className="flex flex-col items-center gap-4">
            <div className="flex space-x-4">
              {hasDynamicSocials ? (
                socialItems.map((social) => (
                  <a
                    key={social.id}
                    href={social.url}
                    className="hover:opacity-80 transition-opacity"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Abrir ${social.title}`}
                  >
                    {getSocialIcon(social.title, { className: "h-6 w-6" })}
                  </a>
                ))
              ) : (
                staticSocialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    className="hover:opacity-80 transition-opacity"
                    aria-label={`Abrir ${social.title}`}
                  >
                    <social.icon className="h-6 w-6" />
                  </a>
                ))
              )}
            </div>
            <p className="text-sm text-gray-400">Sebrae Minas © 2024</p>
          </div>

          {/* Coluna do Logo */}
          <div className="flex justify-center md:justify-end">
            <LogoSebrae className="h-12 text-white" />
          </div>
        </div>
      </div>
      {/* Modal de confirmação ao sair do quiz */}
      <QuizExitDialog
        isOpen={modalOpen}
        onOpenChange={setModalOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </footer>
  );
}

export default Footer; 
